import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Power, History, RotateCcw, X, Save, Search, Tag, MessageCircle } from "lucide-react";

/**
 * V1.28 · KAI · Biblioteca global de plantillas.
 *
 * Uso dual:
 *   - Como sección dentro de Ajustes → KAI → Biblioteca.
 *   - Como pestaña principal del dashboard (dashboard/KAI).
 *
 * Todas las operaciones de escritura (crear, editar, borrar, toggle,
 * restaurar) exigen PIN sudo. El interceptor global de axios + PinContext
 * abren el diálogo de PIN automáticamente ante 440 y reintentan la llamada.
 */
export default function KaiLibraryPanel() {
  const [templates, setTemplates] = useState([]);
  const [variablesCatalog, setVariablesCatalog] = useState({ by_category: {}, variables: [], tags_suggested: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // create | edit
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(null);   // template_id
  const [versions, setVersions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, v] = await Promise.all([
        api.get(`/kai/templates?channel=whatsapp`),
        api.get(`/kai/variables`),
      ]);
      setTemplates(t.data.templates || []);
      setVariablesCatalog(v.data || {});
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => templates.filter((t) => {
    if (t.slug === "__legacy_migrated__") return false;
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    if (tagFilter !== "all" && !(t.tags || []).includes(tagFilter)) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const inTitle = (t.title || "").toLowerCase().includes(q);
      const inBody = (t.body || "").toLowerCase().includes(q);
      const inSlug = (t.slug || "").toLowerCase().includes(q);
      if (!inTitle && !inBody && !inSlug) return false;
    }
    return true;
  }), [templates, search, categoryFilter, tagFilter]);

  const allTags = useMemo(() => {
    const set = new Set(variablesCatalog.tags_suggested || []);
    templates.forEach((t) => (t.tags || []).forEach((tg) => set.add(tg)));
    return Array.from(set).sort();
  }, [templates, variablesCatalog]);

  // ----- Sudo helper -----
  // El interceptor global de axios (PinContext) abre el diálogo de PIN
  // automáticamente si el backend responde 440, y luego reintenta la
  // llamada. Aquí sólo tenemos que traducir errores.
  const withSudo = useCallback(async (operation, fn) => {
    try {
      return await fn();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(formatApiError(detail));
      throw err;
    }
  }, []);

  // ----- Create / Edit -----
  const openCreate = () => {
    setEditorMode("create");
    setEditingTemplate({
      slug: "",
      title: "",
      channel: "whatsapp",
      category: "otros",
      body: "",
      tags: [],
      active: true,
    });
    setEditorOpen(true);
  };

  const openEdit = (t) => {
    setEditorMode("edit");
    setEditingTemplate({ ...t, change_note: "" });
    setEditorOpen(true);
  };

  const saveTemplate = async () => {
    const t = editingTemplate;
    if (!t.title.trim() || !t.body.trim()) {
      toast.error("Título y cuerpo son obligatorios");
      return;
    }
    if (editorMode === "create" && !t.slug.trim()) {
      toast.error("Slug obligatorio (identificador técnico único)");
      return;
    }
    const call = editorMode === "create"
      ? () => api.post("/kai/templates", {
          slug: t.slug.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"),
          title: t.title, channel: "whatsapp", category: t.category,
          body: t.body, tags: t.tags, active: t.active,
        })
      : () => api.put(`/kai/templates/${t.id}`, {
          title: t.title, body: t.body, category: t.category, tags: t.tags,
          active: t.active, change_note: t.change_note || null,
        });
    try {
      await withSudo(editorMode === "create" ? "kai.template.create" : "kai.template.update", call);
      toast.success(editorMode === "create" ? "Plantilla creada" : "Plantilla actualizada (nueva versión)");
      setEditorOpen(false);
      load();
    } catch { /* handled */ }
  };

  const toggleTemplate = async (t) => {
    try {
      await withSudo("kai.template.update", () => api.post(`/kai/templates/${t.id}/toggle`));
      toast.success(t.active ? "Plantilla desactivada" : "Plantilla activada");
      load();
    } catch { /* handled */ }
  };

  const deleteTemplate = async (t) => {
    if (t.is_system) {
      toast.error("Las plantillas del sistema no se pueden borrar. Puedes desactivarlas.");
      return;
    }
    if (!window.confirm(`¿Borrar la plantilla "${t.title}"? Esta acción es irreversible.`)) return;
    try {
      await withSudo("kai.template.delete", () => api.delete(`/kai/templates/${t.id}`));
      toast.success("Plantilla eliminada");
      load();
    } catch { /* handled */ }
  };

  const loadVersions = async (t) => {
    setVersionsOpen(t.id);
    try {
      const { data } = await api.get(`/kai/templates/${t.id}/versions`);
      setVersions(data.versions || []);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const restoreVersion = async (t, version) => {
    if (!window.confirm(`¿Restaurar la versión v${version} sobre la plantilla actual? Se creará una nueva versión.`)) return;
    try {
      await withSudo("kai.template.update", () => api.post(`/kai/templates/${t.id}/restore/${version}`));
      toast.success("Versión restaurada");
      setVersionsOpen(null);
      load();
    } catch { /* handled */ }
  };

  // ----- Variable inserter -----
  const insertVariable = (varKey) => {
    if (!editingTemplate) return;
    const marker = `{{${varKey}}}`;
    setEditingTemplate({ ...editingTemplate, body: (editingTemplate.body || "") + marker });
  };

  return (
    <div className="space-y-4" data-testid="kai-library-panel">
      {/* Cabecera + filtros + acciones */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div className="flex flex-1 gap-2 items-center max-w-3xl">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título, slug o texto…"
              data-testid="kai-library-search"
              className="w-full rounded-full border border-neutral-300 bg-white pl-10 pr-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="kai-library-category"
            className="rounded-full border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Todas las categorías</option>
            {(variablesCatalog.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            data-testid="kai-library-tag"
            className="rounded-full border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="all">Todas las etiquetas</option>
            {allTags.map((tg) => <option key={tg} value={tg}>{tg}</option>)}
          </select>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="btn-primary"
          data-testid="kai-library-create"
        >
          <Plus className="h-4 w-4" /> Nueva plantilla
        </button>
      </div>

      {/* Grid de plantillas */}
      {loading ? (
        <div className="card-surface p-8 text-center text-neutral-500">Cargando biblioteca…</div>
      ) : filtered.length === 0 ? (
        <div className="card-surface p-8 text-center">
          <MessageCircle className="h-8 w-8 mx-auto text-neutral-300 mb-2" />
          <div className="text-sm font-semibold mb-1">No hay plantillas que coincidan</div>
          <p className="text-xs text-neutral-500">Ajusta el filtro o crea una nueva.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`kai-library-card-${t.slug}`} className="rounded-lg border border-neutral-200 bg-white p-3 flex flex-col">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{t.title}</div>
                  <div className="text-[10px] text-neutral-500 font-mono truncate">{t.slug} · v{t.version}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {t.is_system && <span className="text-[9px] font-semibold text-neutral-500 bg-neutral-100 rounded-full px-1.5 py-0.5">SYS</span>}
                  <span className={"text-[9px] font-semibold rounded-full px-1.5 py-0.5 " + (t.active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-600")}>
                    {t.active ? "ACTIVA" : "INACTIVA"}
                  </span>
                </div>
              </div>
              <div className="text-xs text-neutral-700 line-clamp-3 mb-2 whitespace-pre-wrap min-h-[3.6em]">{t.body}</div>
              {(t.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {t.tags.map((tg) => (
                    <span key={tg} className="text-[10px] rounded-full bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
                      <Tag className="h-2 w-2 inline-block mr-0.5" />{tg}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-auto flex items-center gap-1 pt-2 border-t border-neutral-100">
                <button onClick={() => openEdit(t)} className="text-[11px] font-semibold text-neutral-700 hover:text-orange-700 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-orange-50" data-testid={`kai-library-edit-${t.slug}`}>
                  <Pencil className="h-3 w-3" /> Editar
                </button>
                <button onClick={() => loadVersions(t)} className="text-[11px] font-semibold text-neutral-700 hover:text-blue-700 inline-flex items-center gap-1 px-1.5 py-1 rounded hover:bg-blue-50" data-testid={`kai-library-versions-${t.slug}`}>
                  <History className="h-3 w-3" /> Historial
                </button>
                <button onClick={() => toggleTemplate(t)} className={"text-[11px] font-semibold inline-flex items-center gap-1 px-1.5 py-1 rounded " + (t.active ? "text-amber-700 hover:bg-amber-50" : "text-emerald-700 hover:bg-emerald-50")} data-testid={`kai-library-toggle-${t.slug}`}>
                  <Power className="h-3 w-3" /> {t.active ? "Desactivar" : "Activar"}
                </button>
                {!t.is_system && (
                  <button onClick={() => deleteTemplate(t)} className="text-[11px] font-semibold text-red-700 hover:bg-red-50 inline-flex items-center gap-1 px-1.5 py-1 rounded ml-auto" data-testid={`kai-library-delete-${t.slug}`}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      {editorOpen && editingTemplate && (
        <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={() => setEditorOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
              <h3 className="font-semibold text-lg">{editorMode === "create" ? "Nueva plantilla" : `Editar: ${editingTemplate.title}`}</h3>
              <button onClick={() => setEditorOpen(false)} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              {editorMode === "create" && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Slug (identificador técnico)</label>
                  <input
                    type="text"
                    value={editingTemplate.slug}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, slug: e.target.value })}
                    placeholder="p.ej. seguimiento_examen"
                    data-testid="kai-editor-slug"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 font-mono"
                  />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-neutral-700">Título</label>
                <input
                  type="text"
                  value={editingTemplate.title}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, title: e.target.value })}
                  data-testid="kai-editor-title"
                  className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Categoría</label>
                  <select
                    value={editingTemplate.category}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, category: e.target.value })}
                    data-testid="kai-editor-category"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {(variablesCatalog.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Estado</label>
                  <select
                    value={editingTemplate.active ? "1" : "0"}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, active: e.target.value === "1" })}
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="1">Activa</option>
                    <option value="0">Inactiva</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-neutral-700">Etiquetas</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {allTags.map((tg) => {
                    const on = (editingTemplate.tags || []).includes(tg);
                    return (
                      <button
                        key={tg}
                        type="button"
                        onClick={() => {
                          const current = new Set(editingTemplate.tags || []);
                          if (on) current.delete(tg); else current.add(tg);
                          setEditingTemplate({ ...editingTemplate, tags: Array.from(current) });
                        }}
                        className={"text-[11px] rounded-full px-2 py-0.5 border " + (on ? "bg-orange-100 border-orange-300 text-orange-800" : "bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50")}
                      >
                        {tg}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-neutral-700">Cuerpo del mensaje</label>
                  <span className="text-[10px] text-neutral-500">Variables usa: <code className="font-mono">{"{{nombre}}"}</code></span>
                </div>
                <textarea
                  value={editingTemplate.body}
                  onChange={(e) => setEditingTemplate({ ...editingTemplate, body: e.target.value })}
                  data-testid="kai-editor-body"
                  className="w-full min-h-[130px] rounded-lg border border-neutral-300 bg-white p-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="Hola {{nombre}}, ..."
                />
                <div className="mt-2">
                  <div className="text-[10px] font-semibold text-neutral-500 uppercase mb-1">Insertar variable:</div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {Object.entries(variablesCatalog.by_category || {}).map(([cat, vars]) => (
                      <div key={cat} className="w-full">
                        <div className="text-[9px] font-semibold text-neutral-400 uppercase mt-1">{cat}</div>
                        <div className="flex flex-wrap gap-1">
                          {vars.map((v) => (
                            <button
                              key={v.key}
                              type="button"
                              onClick={() => insertVariable(v.key)}
                              className="text-[10px] rounded-full bg-neutral-100 hover:bg-orange-100 hover:text-orange-800 px-2 py-0.5 font-mono transition-colors"
                              title={v.label}
                              data-testid={`kai-editor-insert-${v.key}`}
                            >
                              {"{{"}{v.key}{"}}"}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {editorMode === "edit" && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Nota de cambio (opcional)</label>
                  <input
                    type="text"
                    value={editingTemplate.change_note || ""}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, change_note: e.target.value })}
                    placeholder="Qué has cambiado y por qué"
                    className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
              <button onClick={() => setEditorOpen(false)} className="btn-secondary">Cancelar</button>
              <button onClick={saveTemplate} className="btn-primary" data-testid="kai-editor-save">
                <Save className="h-4 w-4" /> Guardar (requiere PIN sudo)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historial de versiones modal */}
      {versionsOpen && (
        <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={() => setVersionsOpen(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
              <h3 className="font-semibold text-lg flex items-center gap-2"><History className="h-4 w-4" /> Historial de versiones</h3>
              <button onClick={() => setVersionsOpen(null)} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-2" data-testid="kai-versions-list">
              {versions.length === 0 ? (
                <p className="text-sm text-neutral-500">Esta plantilla no tiene versiones anteriores todavía.</p>
              ) : versions.map((v) => (
                <div key={v.id} className="rounded-lg border border-neutral-200 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold">v{v.version}</div>
                    <button
                      onClick={() => {
                        const template = templates.find((t) => t.id === versionsOpen);
                        if (template) restoreVersion(template, v.version);
                      }}
                      data-testid={`kai-version-restore-${v.version}`}
                      className="text-xs font-semibold text-orange-700 hover:text-orange-900 inline-flex items-center gap-1"
                    >
                      <RotateCcw className="h-3 w-3" /> Restaurar
                    </button>
                  </div>
                  <div className="text-[11px] text-neutral-500">
                    {new Date(v.snapshot_at).toLocaleString("es-ES")} · por {v.snapshot_by}
                    {v.change_note && ` · "${v.change_note}"`}
                  </div>
                  <div className="text-xs whitespace-pre-wrap font-mono bg-neutral-50 p-2 rounded mt-2 border border-neutral-200">
                    {v.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
